"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

const GOLD = "#CFAE60";

type Variant = "arena" | "squad" | "daily";

// Restrained, abstract per-mode glyph (geometric, not illustration).
function Glyph({ variant }: { variant: Variant }) {
  if (variant === "arena") {
    return (
      <>
        <polyline points="-36,-20 -12,0 -36,20" />
        <polyline points="36,-20 12,0 36,20" />
        <line x1="0" y1="-28" x2="0" y2="28" stroke="rgba(207,174,96,0.5)" />
      </>
    );
  }
  if (variant === "squad") {
    return (
      <>
        <line x1="-32" y1="-16" x2="-32" y2="16" />
        <line x1="32" y1="-16" x2="32" y2="16" />
        <circle cx="-32" cy="-16" r="6.5" fill={GOLD} stroke="none" />
        <circle cx="-32" cy="16" r="6.5" fill={GOLD} stroke="none" />
        <circle cx="32" cy="-16" r="6.5" fill={GOLD} stroke="none" />
        <circle cx="32" cy="16" r="6.5" fill={GOLD} stroke="none" />
        <line x1="0" y1="-28" x2="0" y2="28" stroke="rgba(207,174,96,0.5)" />
      </>
    );
  }
  return (
    <>
      <circle cx="0" cy="0" r="22" />
      <line x1="-34" y1="0" x2="34" y2="0" stroke="rgba(207,174,96,0.55)" />
      <line x1="0" y1="-34" x2="0" y2="34" stroke="rgba(207,174,96,0.55)" />
      <circle cx="0" cy="0" r="4" fill={GOLD} stroke="none" />
    </>
  );
}

// candle heights per mode (an up-trend with variation, so each card differs)
const CANDLES: Record<Variant, number[]> = {
  arena: [34, 30, 46, 40, 58, 52, 70, 64, 84, 78, 96],
  squad: [40, 52, 46, 64, 56, 76, 70, 88, 80, 98, 92],
  daily: [30, 44, 38, 50, 66, 58, 72, 64, 82, 90, 104],
};

function Candles({ variant, reduced }: { variant: Variant; reduced: boolean }) {
  const hs = CANDLES[variant];
  const step = 480 / hs.length;
  return (
    <>
      {hs.map((h, i) => {
        const x = i * step + step / 2;
        const base = 264;
        const top = base - h;
        const last = i === hs.length - 1;
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={top - 12} y2={base + 10} stroke="rgba(207,174,96,0.35)" strokeWidth="1.5" />
            <motion.rect
              x={x - 7}
              y={top}
              width={14}
              height={h}
              rx={2}
              fill={last ? "rgba(207,174,96,0.85)" : "rgba(207,174,96,0.5)"}
              style={{ transformBox: "fill-box", transformOrigin: "bottom" }}
              animate={reduced ? undefined : { scaleY: [1, 1.16, 0.86, 1] }}
              transition={
                reduced
                  ? undefined
                  : { duration: 3.2 + (i % 4) * 0.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.18 }
              }
            />
          </g>
        );
      })}
    </>
  );
}

// Animated, design-led mode visual — parallax on scroll, a living candlestick
// chart, a breathing glow, and a hover lift. Keeps the brand's minimal register
// (no illustration) but reads alive, not like a static slide.
export function ModeArt({ variant }: { variant: Variant }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion() ?? false;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const chartY = useTransform(scrollYProgress, [0, 1], reduced ? ["0%", "0%"] : ["10%", "-10%"]);
  const glyphY = useTransform(scrollYProgress, [0, 1], reduced ? ["0%", "0%"] : ["-7%", "7%"]);

  return (
    <motion.div
      ref={ref}
      initial={reduced ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -10% 0px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      whileHover={reduced ? undefined : { y: -5 }}
      className="group relative aspect-[16/10] overflow-hidden rounded-lg border border-line-gold bg-elevated shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] transition-colors duration-500 hover:border-gold/45"
    >
      <div className="pointer-events-none absolute inset-0 z-20 rounded-lg ring-1 ring-inset ring-gold/10" />

      {/* faint grid */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(207,174,96,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(207,174,96,0.06) 1px, transparent 1px)",
          backgroundSize: "30px 30px",
        }}
      />

      {/* breathing glow */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -inset-x-12 -top-1/3 h-2/3 transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: "radial-gradient(closest-side, rgba(207,174,96,0.20), transparent)", opacity: 0.7 }}
        animate={reduced ? undefined : { opacity: [0.55, 0.9, 0.55] }}
        transition={reduced ? undefined : { duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* living candlestick chart (parallax layer) */}
      <motion.svg
        aria-hidden
        viewBox="0 0 480 300"
        preserveAspectRatio="xMidYMid slice"
        style={{ y: chartY }}
        className="absolute inset-0 h-full w-full"
      >
        <Candles variant={variant} reduced={reduced} />
      </motion.svg>

      {/* glyph (parallax layer) */}
      <motion.div style={{ y: glyphY }} className="absolute inset-0 z-10 flex items-center justify-center">
        <motion.svg
          width="160"
          height="120"
          viewBox="-80 -60 160 120"
          initial={reduced ? false : { opacity: 0, scale: 0.85 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "0px 0px -15% 0px" }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          className="drop-shadow-[0_0_18px_rgba(207,174,96,0.25)]"
        >
          <g stroke={GOLD} strokeWidth={4} strokeLinecap="round" strokeLinejoin="round" fill="none">
            <Glyph variant={variant} />
          </g>
        </motion.svg>
      </motion.div>
    </motion.div>
  );
}
